/**
 * Breaking Hack Mini App — Telegram WebApp bridge.
 *
 * This file only reads what Telegram's own `telegram-web-app.js` script exposes
 * on `window.Telegram.WebApp` (see index.html). It never invents data.
 *
 * SECURITY: `initData` is an opaque, signed string. This client NEVER parses or
 * trusts its fields itself — per PRODUCT_REQUIREMENTS.md/TECHNICAL_DESIGN.md,
 * identity must be established by a server verifying the HMAC signature with
 * the bot token, exactly like the existing `assertNotMainnetWrite`-style
 * fail-closed pattern used elsewhere in this project. The server endpoint that
 * does this verification does not exist yet (see README.md "Not built yet").
 * Until it does, `initData` below is only ever sent to that future endpoint,
 * never read locally for authorization decisions.
 */

export interface TelegramWebApp {
  readonly initData: string;
  readonly platform: string;
  readonly colorScheme: 'light' | 'dark';
  ready(): void;
  expand(): void;
  close(): void;
}

interface TelegramGlobal {
  WebApp?: TelegramWebApp;
}

declare global {
  interface Window {
    Telegram?: TelegramGlobal;
  }
}

/** A safe stand-in used outside Telegram (plain browser, local dev, tests). */
const stubWebApp: TelegramWebApp = {
  initData: '',
  platform: 'unknown',
  colorScheme: 'dark',
  ready: () => undefined,
  expand: () => undefined,
  close: () => undefined,
};

/**
 * Returns the real Telegram WebApp bridge when this page runs inside
 * Telegram, or a harmless stub otherwise. Never throws.
 */
export function getTelegramWebApp(): TelegramWebApp {
  return window.Telegram?.WebApp ?? stubWebApp;
}

/** True only when a real Telegram session context is present. */
export function isInsideTelegram(): boolean {
  return window.Telegram?.WebApp !== undefined && window.Telegram.WebApp.initData.length > 0;
}
